import { useTranslation } from 'react-i18next';
import { mergeClasses } from '@fluentui/react-components';
import { useMetricTileStyles } from './MetricTile.styles';

/** Props for {@link MetricTile}. */
export interface MetricTileProps {
  /** Metric group label (e.g. "Meetings" or "Attendees"). */
  label: string;
  /** Requested (target) value. */
  requestedValue: number;
  /** Completed (actual) value. */
  completedValue: number;
}

/**
 * Displays a single planner metric as a requested-vs-completed pair with a
 * proportional progress bar (completed ÷ requested). Values are server-computed
 * and only rendered here.
 */
export const MetricTile = ({ label, requestedValue, completedValue }: MetricTileProps) => {
  const styles = useMetricTileStyles();
  const { t } = useTranslation('metricsReport');

  const ratio = requestedValue > 0 ? Math.min(completedValue / requestedValue, 1) : 0;
  const fillPercent = Math.round(ratio * 100);

  return (
    <section className={styles.root} aria-label={label}>
      <h3 className={styles.label}>{label}</h3>
      <div className={styles.values}>
        <div className={styles.valueGroup}>
          <span className={styles.valueCaption}>{t('tile.requestedLabel')}</span>
          <span className={styles.value} data-testid="metric-requested">
            {requestedValue.toLocaleString()}
          </span>
        </div>
        <div className={styles.valueGroup}>
          <span className={styles.valueCaption}>{t('tile.completedLabel')}</span>
          <span
            className={mergeClasses(styles.value, styles.completedValue)}
            data-testid="metric-completed"
          >
            {completedValue.toLocaleString()}
          </span>
        </div>
      </div>
      <div
        className={styles.barTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={requestedValue}
        aria-valuenow={completedValue}
        aria-label={label}
      >
        <span className={styles.barFill} style={{ width: `${fillPercent}%` }} />
      </div>
    </section>
  );
};

MetricTile.displayName = 'MetricTile';
